import React, {ReactNode, useEffect, useState} from 'react'
import {
  InfoIcon,
  LayoutDashboardIcon,
  MenuIcon,
  MoonIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  ReceiptTextIcon,
  SunIcon,
  type LucideIcon,
} from 'lucide-react'
import { useContent, useSetFlash } from '@thoughtbot/superglue'
import { useAppFlash } from '@javascript/flash'
import { Alert, AlertTitle } from '@javascript/components/ui/alert'
import { Form, type FormProps } from '@javascript/components/Inputs'
import { BrandMark } from '@javascript/components/BrandMark'
import { CookieConsentBanner, openCookiePreferences } from '@javascript/components/CookieConsentBanner'
import { HostFlash } from '@javascript/components/HostFlash'
import { Button } from '@javascript/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@javascript/components/ui/sheet'
import { cn } from '@javascript/components/utils'

const flashFeedbacks = [
  {key: 'error', state: 'error'},
  {key: 'alert', state: 'error'},
  {key: 'success', state: 'success'},
  {key: 'notice', state: 'notice'},
] as const

type HostLayoutContent = {
  currentUser?: {
    email: string
    confirmationPending?: boolean
    confirmationPath?: string
  }
  logoutForm?: FormProps
  hideSharedHeader?: boolean
  layoutWidth?: 'default' | 'wide' | 'public'
  title?: string
}

const hostContentWidths = {
  default: 'max-w-3xl',
  wide: 'max-w-7xl',
  public: 'max-w-6xl',
} as const

const sidebarStorageKey = 'weddang-host-sidebar-collapsed'

// A destination is marked current when the page's `title` prop equals its label,
// so each label is also the `json.title` of the page it points at.
type HostDestination = {
  label: string
  href: string
  icon: LucideIcon
}

const hostDestinations: HostDestination[] = [
  {label: 'Dashboard', href: '/dashboard', icon: LayoutDashboardIcon},
  {label: 'Transactions', href: '/transactions', icon: ReceiptTextIcon},
]

export const Layout = ({children}: {children: ReactNode}) => {
  const {
    currentUser,
    logoutForm,
    hideSharedHeader,
    layoutWidth,
    title,
  } = useContent<HostLayoutContent>()
  const flash = useAppFlash()
  const { clearFlash } = useSetFlash()
  const [dark, setDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  )
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => typeof window !== 'undefined' && window.localStorage.getItem(sidebarStorageKey) === 'true',
  )
  const feedback = flashFeedbacks.find(({key}) => flash[key])
  const showChrome = !hideSharedHeader && !!currentUser
  const hostContentWidth = hostContentWidths[layoutWidth ?? 'default']

  useEffect(() => {
    document.title = title ? `${title} · Goldypaper` : 'Goldypaper'
  }, [title])

  const toggleTheme = () => {
    const nextDark = !dark

    document.documentElement.classList.toggle('dark', nextDark)
    localStorage.setItem('weddang-host-theme', nextDark ? 'dark' : 'light')
    setDark(nextDark)
  }

  const toggleSidebar = () => {
    const nextCollapsed = !sidebarCollapsed

    window.localStorage.setItem(sidebarStorageKey, String(nextCollapsed))
    setSidebarCollapsed(nextCollapsed)
  }

  // h-dvh, not h-screen: 100vh is the viewport with a mobile browser's
  // address bar collapsed, so on Android Chrome with the bar showing this
  // shrink-0 shell rendered taller than what was actually visible — with
  // overflow-hidden on this wrapper (only `main` scrolls), the bottom-most
  // child (the cookie consent banner) was clipped off-screen with no way to
  // scroll to it. 100dvh tracks the real visible viewport instead.
  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-muted">
      {showChrome && (
        <>
          <header data-shared-header className="shrink-0 border-b bg-background">
            <nav
              aria-label="Main"
              className="flex w-full items-center justify-between gap-1 px-2 py-3 sm:gap-2 sm:px-4"
            >
              <div className="flex min-w-0 items-center gap-1 sm:gap-2">
                <a href="/" className="shrink-0 font-serif text-lg font-semibold tracking-tight text-foreground">
                  <BrandMark size="md" />
                </a>
                <MobileHostNavigation pageTitle={title} />
              </div>

              <div className="flex min-w-0 items-center gap-1.5">
                <span data-host-account-email className="hidden min-w-0 max-w-64 truncate text-sm text-muted-foreground md:block">
                  {currentUser.email}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="shrink-0"
                  aria-label={`Switch to ${dark ? 'light' : 'dark'} mode`}
                  title={`Switch to ${dark ? 'light' : 'dark'} mode`}
                  onClick={toggleTheme}
                >
                  {dark ? <SunIcon /> : <MoonIcon />}
                </Button>
                {/* No data-sg-visit: logout must full-reload to flush in-memory Superglue state (see reference/setup.md Security). */}
                {logoutForm && (
                  <Form {...logoutForm.form} extras={logoutForm.extras}>
                    <Button type="submit" variant="outline" size="sm">Log out</Button>
                  </Form>
                )}
              </div>
            </nav>
          </header>

          {currentUser.confirmationPending && currentUser.confirmationPath && (
            <div data-testid="unconfirmed-account-notice" className="border-b border-primary/25 bg-background">
              <Alert className={cn(
                'mx-auto w-full rounded-none border-0 bg-transparent px-4 py-3 text-primary shadow-none',
                hostContentWidth,
              )}>
                <InfoIcon />
                <AlertTitle>
                  Confirm {currentUser.email} to create, save, or publish your invitation. These actions are blocked until your email is confirmed.{' '}
                  <a href={currentUser.confirmationPath} data-sg-visit="true">Resend confirmation</a>
                </AlertTitle>
              </Alert>
            </div>
          )}
        </>
      )}

      {feedback && (
        <HostFlash
          state={feedback.state}
          message={flash[feedback.key] ?? ''}
          onDismiss={() => clearFlash(feedback.key)}
        />
      )}

      <div className="flex min-h-0 min-w-0 flex-1">
        {showChrome && (
          <HostSidebar pageTitle={title} collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
        )}
        {/* `relative` is load-bearing: the scrolling region must also be the
            containing block for what it scrolls. An absolutely positioned
            descendant without one resolves against the viewport instead, so it
            escapes this clip and stretches the document — Radix hides a bubble
            input behind every Switch and Checkbox that way, and one of them puts
            a second scrollbar beside the first. */}
        <main className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
          {children}

          {showChrome && (
            <footer data-host-footer className="mt-auto border-t bg-background">
              <div className={cn(
                'mx-auto flex w-full flex-col items-start gap-3 px-4 py-4 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between',
                hostContentWidth,
              )}>
                <div>
                  <BrandMark size="sm" className="font-serif font-semibold text-foreground" />
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <a href="/terms" className="transition-colors hover:text-foreground">Terms of Service</a>
                  <a href="/privacy" className="transition-colors hover:text-foreground">Privacy Policy</a>
                  <a href="/contact" className="transition-colors hover:text-foreground">Contact</a>
                  <button
                    type="button"
                    onClick={openCookiePreferences}
                    className="transition-colors hover:text-foreground"
                  >
                    Cookie preferences
                  </button>
                </div>
              </div>
            </footer>
          )}
        </main>
      </div>

      <CookieConsentBanner />
    </div>
  )
}

function HostSidebar({
  pageTitle,
  collapsed,
  onToggle,
}: {
  pageTitle?: string
  collapsed: boolean
  onToggle: () => void
}) {
  return (
    <aside
      data-host-sidebar
      data-collapsed={collapsed}
      className={cn(
        'hidden shrink-0 flex-col border-r bg-background transition-[width] duration-200 ease-in-out lg:flex',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <div className="flex h-full flex-col p-3">
        <HostNavigation pageTitle={pageTitle} collapsed={collapsed} />
        <SidebarToggle collapsed={collapsed} onToggle={onToggle} className="mt-auto border-t pt-3" />
      </div>
    </aside>
  )
}

// Below `lg` the sidebar is a panel, not a column, so it offers destinations only:
// there is no room to give back, and the collapse choice belongs to the column.
function MobileHostNavigation({pageTitle}: {pageTitle?: string}) {
  const [open, setOpen] = useState(false)

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0 lg:hidden"
          aria-label="Open navigation"
          title="Open navigation"
        >
          <MenuIcon aria-hidden="true" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Navigation</SheetTitle>
          <SheetDescription>Choose where you want to go.</SheetDescription>
        </SheetHeader>
        <div className="px-4 pb-6">
          <HostNavigation pageTitle={pageTitle} collapsed={false} onNavigate={() => setOpen(false)} />
        </div>
      </SheetContent>
    </Sheet>
  )
}

function HostNavigation({
  pageTitle,
  collapsed,
  onNavigate,
}: {
  pageTitle?: string
  collapsed: boolean
  onNavigate?: () => void
}) {
  return (
    <nav aria-label="Host navigation">
      <ul className="space-y-1">
        {hostDestinations.map((destination) => (
          <li key={destination.href}>
            <HostDestination
              destination={destination}
              current={pageTitle === destination.label}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          </li>
        ))}
      </ul>
    </nav>
  )
}

function HostDestination({
  destination,
  current,
  collapsed,
  onNavigate,
}: {
  destination: HostDestination
  current: boolean
  collapsed: boolean
  onNavigate?: () => void
}) {
  const Icon = destination.icon

  return (
    <a
      href={destination.href}
      data-sg-visit="true"
      aria-current={current ? 'page' : undefined}
      aria-label={collapsed ? destination.label : undefined}
      title={collapsed ? destination.label : undefined}
      onClick={onNavigate}
      className={cn(
        'flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 active:bg-accent/80',
        collapsed && 'justify-center px-2',
        current ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-accent/60',
      )}
    >
      <Icon aria-hidden="true" className="size-4 shrink-0" />
      <span className={collapsed ? 'sr-only' : 'truncate'}>{destination.label}</span>
    </a>
  )
}

function SidebarToggle({
  collapsed,
  onToggle,
  className,
}: {
  collapsed: boolean
  onToggle: () => void
  className?: string
}) {
  const label = collapsed ? 'Expand sidebar' : 'Collapse sidebar'
  const Icon = collapsed ? PanelLeftOpenIcon : PanelLeftCloseIcon

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn('w-full justify-start', collapsed && 'justify-center px-0', className)}
      aria-label={label}
      title={label}
      onClick={onToggle}
    >
      <Icon aria-hidden="true" />
      <span className={collapsed ? 'sr-only' : undefined}>{label}</span>
    </Button>
  )
}
